"use client";

import { type FormEvent, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import searchIcon from "public/icons/search.svg";
import { CONTENT_SIDE_LINES, CONTENT_WIDTH } from "@/shared/components/BlockWrapper";
import { lockPageScroll, unlockPageScroll } from "@/lib/scrollLock";
import { cn } from "@/lib/utils";
import { SearchSubmitButton } from "./SearchSubmitButton";

interface HeaderSearchToggleProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  className?: string;
}

/** Figma block_search в шапке: открывается и закрывается по клику на иконку, закрытие по клику вне панели. */
export function HeaderSearchToggle({
  isOpen,
  onOpenChange,
  className,
}: HeaderSearchToggleProps) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const triggerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        overlayRef.current?.contains(target)
      ) {
        return;
      }
      onOpenChange(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    lockPageScroll();

    return () => {
      unlockPageScroll();
    };
  }, [isOpen]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (query.trim().length < 2) return;
    router.push(`/search?q=${encodeURIComponent(query.trim())}`);
    onOpenChange(false);
    setQuery("");
  };

  const handleToggle = () => {
    onOpenChange(!isOpen);
  };

  return (
    <div ref={triggerRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={handleToggle}
        className="flex size-6 items-center justify-center text-foreground transition-colors hover:text-primary"
        aria-label="Поиск по сайту"
        aria-expanded={isOpen}
      >
        <Image src={searchIcon} alt="" width={24} height={24} className="size-6" />
      </button>

      {isOpen && (
        <div ref={overlayRef}>
          <div
            className="fixed inset-x-0 bottom-0 top-[var(--header-height)] z-30 bg-secondary/50"
            aria-hidden
            onClick={() => onOpenChange(false)}
          />

          <div className="fixed inset-x-0 top-[var(--header-height)] z-50 border-b border-border bg-header-top-fg">
            <form
              onSubmit={handleSubmit}
              className={cn(CONTENT_WIDTH, CONTENT_SIDE_LINES)}
            >
              <div className="flex flex-col items-center gap-6 px-4 py-6 md:h-20 md:flex-row md:justify-center md:gap-6 md:px-6 md:py-0 desktop:gap-10 desktop:px-20 desktop:py-10">
                <label className="flex w-full flex-col gap-2 md:max-w-[610px] desktop:max-w-[800px]">
                  <span className="sr-only">Поисковый запрос</span>
                  <input
                    ref={inputRef}
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Введите ваш запрос"
                    className="w-full bg-transparent font-sans text-base text-tag-foreground placeholder:text-tag-foreground focus:outline-none"
                  />
                  <span className="block h-px w-full bg-tag-foreground" aria-hidden />
                </label>

                <SearchSubmitButton />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
